import { useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import {
  ScheduleSelect,
  useSyncScheduleContext,
} from "@/components/elements/SyncScheduler";
import Tooltip from "@/components/elements/Tooltip";
import {
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@/components/elements/input/ListboxDropdown";
import { useCurrentPlan } from "@/features/subscription";
import cn from "@/helpers/classNames";
import { ListboxProps } from "@headlessui/react";
import { ChevronDownIcon } from "lucide-react";

import { CronOption } from "../WeldSyncScheduler";
import { UnavailableCronOptionTooltipContent } from "./UnavailableCronOptionTooltipContent";

export function SyncScheduleListbox(
  props: ListboxProps<"div", CronOption, CronOption>,
) {
  const weldPlan = useCurrentPlan();
  const { options, selectedOption } = useSyncScheduleContext<CronOption>();

  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLUListElement | null>(
    null,
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "bottom-start",
    modifiers: [
      { name: "preventOverflow", enabled: true },
      { name: "offset", options: { offset: [0, 10] } },
    ],
  });

  return (
    <ScheduleSelect value={selectedOption} {...props}>
      <>
        <ListboxButton
          ref={setReferenceElement}
          className={cn("w-auto", {
            "text-gray-500": selectedOption == null,
          })}
          iconRight={<ChevronDownIcon className="size-5" />}
        >
          {selectedOption?.label ?? "Select a schedule"}
        </ListboxButton>

        {createPortal(
          <ListboxOptions
            className="max-w-64"
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
          >
            {options.map((option) => {
              if (!option.disabled) {
                return (
                  <ListboxOption
                    key={option.value}
                    value={option}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </ListboxOption>
                );
              }
              return (
                <li className="relative" key={option.value}>
                  <Tooltip
                    content={
                      <UnavailableCronOptionTooltipContent
                        currentPlanName={weldPlan.name}
                        targetPlan={option.targetPlan}
                      />
                    }
                  >
                    <div>
                      <ListboxOption
                        as="div"
                        value={option}
                        disabled={option.disabled}
                      >
                        <span>{option.label}</span>
                      </ListboxOption>
                    </div>
                  </Tooltip>
                </li>
              );
            })}
          </ListboxOptions>,
          document.body,
        )}
      </>
    </ScheduleSelect>
  );
}
